import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { LabelButton, TextLink } from '@teamsnap/snap-ui';
import { AuthContext, User } from 'auth';
import {
  DemoType,
  TeamsnapAppConfirmationModal,
  TeamsnapAppFeatureCallout,
  useTeamsnapAppAdUpsellActions,
} from './shared';

const learnMoreLink =
  'https://www.teamsnap.com/for-business/features/coaches-pack/';

type RouteParams = { orgId: string };

const CoachesPack: React.FC<RouteComponentProps<RouteParams>> = ({
  match: {
    params: { orgId },
  },
}) => {
  React.useEffect(() => {
    window.scrollTo({
      top: 0,
    });
  }, []);

  const { user } = React.useContext(AuthContext);
  const [showModal, toggleModal] = React.useState(false);

  const [learnMore, sendEmailRequest] = useTeamsnapAppAdUpsellActions(
    user as User,
    DemoType.CoachesPack,
    orgId
  );

  return (
    <div id="coaches-pack">
      <TeamsnapAppConfirmationModal
        show={showModal}
        onClose={() => toggleModal(false)}
      >
        <h2 className="sui-heading-md sui-mb-2">Got it!</h2>
        <p>
          We will email you soon to set up your demo and answer any questions
          you might have.
        </p>
      </TeamsnapAppConfirmationModal>
      <div className="page-content sui-p-4">
        <header className="sui-flex sui-flex-col d:sui-flex-row d:sui-justify-between sui-mb-4 sui-gap-2">
          <div>
            <h2 className="sui-heading-md">Coaches Pack</h2>
            <TextLink
              href={learnMoreLink}
              onClick={(e) => {
                e.preventDefault();
                learnMore(learnMoreLink);
              }}
            >
              Learn More
            </TextLink>
          </div>
          <div>
            <LabelButton
              icon="add"
              iconPosition="left"
              labelText="Request a demo"
              sentiment="default"
              size="default"
              variantType="primary"
              onClick={() => sendEmailRequest(() => toggleModal(true))}
            />
          </div>
        </header>
        <div className="sui-flex-col d:sui-flex-row sui-flex sui-gap-3">
          <TeamsnapAppFeatureCallout>
            Access to premium drills and free activity library
          </TeamsnapAppFeatureCallout>
          <TeamsnapAppFeatureCallout>
            Developmentally focused week-by-week curriculums
          </TeamsnapAppFeatureCallout>
          <TeamsnapAppFeatureCallout>
            Easily add to any TeamSnap for Business subscription
          </TeamsnapAppFeatureCallout>
        </div>
        <div className="sui-flex sui-flex-col d:sui-flex-row sui-gap-[64px] sui-mt-4 d:sui-mt-6 sui-items-center">
          <div className="sui-w-full d:sui-w-1/2 sui-body">
            <h1 className="sui-heading-md sui-mb-2">
              Struggling to recruit and retain coaches?
            </h1>
            <p className="sui-mb-2">
              TeamSnap is here to help! Coaching curriculums on the new
              TeamSnap+ Coaches Pack empower every coach in your organization to
              grow future stars with access to:
            </p>
            <ul className="sui-my-2 sui-pl-4">
              <li className="sui-my-1">
                Premium drills and free activity library, backed by partners
                like MLB, Jr. NBA, PLL, MLS, US Youth Soccer, and more!
              </li>
              <li className="sui-my-1">
                Developmentally focused week-by-week coaching curriculums
              </li>
              <li className="sui-my-1">
                The best version of our #1-rated mobile app.
              </li>
            </ul>
            <p className="sui-mb-2">
              Set your coaches up for instant success and take skill development
              in your organization to the next level. Talk to our account
              management team today to learn more!
            </p>
          </div>
          <div className="sui-w-full d:sui-w-1/2">
            <iframe
              className="sui-w-full"
              src="https://player.vimeo.com/video/932092421"
              style={{ aspectRatio: '16 / 9' }}
              allow="fullscreen"
              allowFullScreen
              title="coaches-pack-video"
            >
            </iframe>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoachesPack;
